import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import {
  FiSettings,
  FiMapPin,
  FiCreditCard,
  FiSave,
  FiEdit2,
  FiBriefcase,
  FiPhone,
  FiMail,
  FiGlobe,
  FiHash,
  FiImage,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";
import { API_URL } from "../../../config";
import "./Details.css";

const CompanyDetailsPage = () => {
  const [company, setCompany] = useState({
    name: "",
    address: { street: "", city: "", state: "", zip: "", country: "" },
    phone: "",
    email: "",
    website: "",
    taxId: "",
    bank: { bankName: "", accountNumber: "", ifsc: "" },
    logoUrl: "",
  });
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/getCompnayDetails`)
      .then((res) => res.data?.[0] && setCompany(res.data[0]))
      .catch((error) => console.error("Error fetching company:", error));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      setCompany({ ...company, address: { ...company.address, [name.split(".")[1]]: value } });
    } else if (name.startsWith("bank.")) {
      setCompany({ ...company, bank: { ...company.bank, [name.split(".")[1]]: value } });
    } else {
      setCompany({ ...company, [name]: value });
    }
  };

  const handleSave = async () => {
    try {
      let response;
      if (company._id) {
        response = await axios.put(`${API_URL}/api/updateCompnay/${company._id}`, company, {
          headers: { "Content-Type": "application/json" },
        });
      } else {
        response = await axios.post(`${API_URL}/api/companyDetails`, company, {
          headers: { "Content-Type": "application/json" },
        });
      }
      setCompany(response.data);
      setEditing(false);
    } catch (error) {
      console.error("Error saving company details:", error);
      alert("Error saving company details.");
    }
  };

  const handleRemoveLogo = () => {
    setCompany({ ...company, logoUrl: "" });
  };

  const filledMainFields = [
    company.name,
    company.phone,
    company.email,
    company.website,
    company.taxId,
    company.address?.street,
    company.address?.city,
    company.address?.state,
    company.address?.zip,
    company.address?.country,
    company.bank?.bankName,
    company.bank?.accountNumber,
    company.bank?.ifsc,
  ].filter((value) => String(value || "").trim() !== "").length;

  const profileCompletion = Math.round((filledMainFields / 13) * 100);
  const contactReady = Boolean(company.phone && company.email && company.website);
  const bankingReady = Boolean(company.bank?.bankName && company.bank?.accountNumber && company.bank?.ifsc);

  return (
    <div className="settings-page">
      <section className="settings-hero">
        <div className="settings-hero-content">
          <p className="settings-overline">Admin Configuration</p>
          <h3>Company Settings Dashboard</h3>
          <p className="settings-subline">Manage company profile, address, and bank details</p>
        </div>
        <div className="settings-hero-right">
          <div className="settings-hero-icon">
            <FiSettings />
          </div>
          {editing ? (
            <button className="btn px-4 settings-top-action" style={{ background: "#003e6d", borderColor: "#003e6d", color: "#fff" }} onClick={handleSave}>
              <FiSave className="me-2" />
              Save Changes
            </button>
          ) : (
            <button className="btn btn-light px-4 settings-top-action" onClick={() => setEditing(true)}>
              <FiEdit2 className="me-2" />
              Edit Profile
            </button>
          )}
        </div>
      </section>

      <section className="settings-kpis">
        <div className="settings-kpi-card">
          <div className="settings-kpi-icon icon-indigo">
            <FiBriefcase />
          </div>
          <div>
            <p className="settings-kpi-label">Profile Completion</p>
            <h4>{profileCompletion}%</h4>
          </div>
        </div>
        <div className="settings-kpi-card">
          <div className={`settings-kpi-icon ${contactReady ? "icon-green" : "icon-amber"}`}>
            {contactReady ? <FiCheckCircle /> : <FiAlertCircle />}
          </div>
          <div>
            <p className="settings-kpi-label">Contact Setup</p>
            <h4>{contactReady ? "Ready" : "Needs Update"}</h4>
          </div>
        </div>
        <div className="settings-kpi-card">
          <div className={`settings-kpi-icon ${bankingReady ? "icon-green" : "icon-amber"}`}>
            {bankingReady ? <FiCheckCircle /> : <FiAlertCircle />}
          </div>
          <div>
            <p className="settings-kpi-label">Banking Status</p>
            <h4>{bankingReady ? "Verified" : "Incomplete"}</h4>
          </div>
        </div>
      </section>

      <section className="settings-panel">
        <div className="settings-grid">
          <div className="settings-card">
            <h5>
              <FiBriefcase />
              Company Profile
            </h5>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Company Name</label>
                <input
                  name="name"
                  value={company.name}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-control settings-input"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Phone</label>
                <div className="settings-input-wrap">
                  <FiPhone className="settings-input-icon" />
                  <input
                    name="phone"
                    value={company.phone}
                    onChange={handleChange}
                    disabled={!editing}
                    className="form-control settings-input has-icon"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Email</label>
                <div className="settings-input-wrap">
                  <FiMail className="settings-input-icon" />
                  <input
                    name="email"
                    value={company.email}
                    onChange={handleChange}
                    disabled={!editing}
                    className="form-control settings-input has-icon"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Website</label>
                <div className="settings-input-wrap">
                  <FiGlobe className="settings-input-icon" />
                  <input
                    name="website"
                    value={company.website}
                    onChange={handleChange}
                    disabled={!editing}
                    className="form-control settings-input has-icon"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Tax ID (GST/VAT)</label>
                <div className="settings-input-wrap">
                  <FiHash className="settings-input-icon" />
                  <input
                    name="taxId"
                    value={company.taxId}
                    onChange={handleChange}
                    disabled={!editing}
                    className="form-control settings-input has-icon"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Logo URL</label>
                <div className="settings-input-wrap">
                  <FiImage className="settings-input-icon" />
                  <input
                    name="logoUrl"
                    value={company.logoUrl || ""}
                    onChange={handleChange}
                    disabled={!editing}
                    className="form-control settings-input has-icon"
                  />
                </div>
              </div>
              {company.logoUrl ? (
                <div className="col-12 d-flex align-items-center gap-3">
                  <img src={company.logoUrl} alt="Company Logo" className="settings-logo-preview" />
                  {editing ? (
                    <button className="btn btn-outline-danger btn-sm" type="button" onClick={handleRemoveLogo}>
                      Remove Logo
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="settings-card">
            <h5>
              <FiMapPin /> Address
            </h5>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Street</label>
                <input
                  name="address.street"
                  value={company.address?.street || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-control settings-input"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">City</label>
                <input
                  name="address.city"
                  value={company.address?.city || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-control settings-input"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">State</label>
                <input
                  name="address.state"
                  value={company.address?.state || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-control settings-input"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">ZIP</label>
                <input
                  name="address.zip"
                  value={company.address?.zip || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-control settings-input"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Country</label>
                <input
                  name="address.country"
                  value={company.address?.country || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-control settings-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="settings-grid settings-grid-bottom">
          <div className="settings-card">
            <h5>
              <FiCreditCard /> Bank Details
            </h5>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Bank Name</label>
                <input
                  name="bank.bankName"
                  value={company.bank?.bankName || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-control settings-input"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Account Number</label>
                <input
                  name="bank.accountNumber"
                  value={company.bank?.accountNumber || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-control settings-input"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">IFSC</label>
                <input
                  name="bank.ifsc"
                  value={company.bank?.ifsc || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-control settings-input"
                />
              </div>
            </div>
          </div>

          <div className="settings-card settings-note-card">
            <h5>
              <FiMapPin />
              Location Snapshot
            </h5>
            <div className="settings-note-box">
              <p className="mb-1 fw-semibold">{company.name || "Company Name"}</p>
              <p className="mb-1 text-muted">{company.address?.street || "Street address not set"}</p>
              <p className="mb-1 text-muted">
                {[company.address?.city, company.address?.state, company.address?.zip]
                  .filter(Boolean)
                  .join(", ") || "City, State, ZIP"}
              </p>
              <p className="mb-0 text-muted">{company.address?.country || "Country not set"}</p>
            </div>
          </div>
        </div>

        <div className="settings-actions">
          {editing ? (
            <button className="btn px-4" style={{ background: "#003e6d", borderColor: "#003e6d", color: "#fff" }} onClick={handleSave}>
              <FiSave className="me-2" />
              Save
            </button>
          ) : (
            <button className="btn px-4" style={{ background: "#003e6d", borderColor: "#003e6d", color: "#fff" }} onClick={() => setEditing(true)}>
              <FiEdit2 className="me-2" />
              Edit
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default CompanyDetailsPage;
